import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { MeResponse } from "@edu/shared";
import * as api from "./api.ts";
/**
 * Очередь заметок о неточностях (`docs/feedback-notes-design.md`, раздел 6.5).
 *
 * ⚠️ Доставка перед выходом живёт ЗДЕСЬ, а не в провайдере фичи, и по-другому
 * быть не может. Провайдер `FeedbackProvider` стоит НИЖЕ `SessionProvider`
 * (см. `App.tsx`), то есть по направлению зависимости про выход он не знает и
 * узнать не может: это `signOut` зовёт очередь, а не наоборот. Ровно поэтому
 * очередь сделана модульным слоем без React, и отсюда она дёргается напрямую.
 */
import { flushBeforeSignOut, setQueueUser } from "../features/feedback/queue.ts";
/**
 * Очередь повторений карточек (7.4 `docs/flashcards-design.md`): тот же довод,
 * что у заметок выше. Модуль без React и без `@edu/shared/flashcards`, в
 * стартовом чанке он только досылает строки и стирает кэш колод.
 */
import { flushFlashcardsBeforeSignOut, setFlashcardsUser } from "../features/flashcards/review-flush.ts";
import {
  VIEW_PATH,
  announceSessionChanged,
  onSessionChanged,
  reloadOnSessionChange,
  sessionIdentity,
} from "./view.ts";

/**
 * Сессия приложения.
 *
 * Токен в localStorage не кладём вообще: он живёт в cookie `__Host-`, которую
 * JS не читает (HttpOnly). Здесь только то, что сервер рассказал о владельце
 * этой cookie. Отсюда же следует, что «выйти» это запрос к серверу, а не
 * удаление строки в браузере: отзыв обязан быть мгновенным и на всех вкладках.
 *
 * Имя вводится ровно один раз, при входе. Дальше оно приходит из app_user, и
 * ни один экран его больше не спрашивает: до M6 клиент присылал имя и роль в
 * каждом запросе токена, и это была дыра, а не удобство.
 */

export type SessionState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "signed"; me: MeResponse };

interface SessionApi {
  state: SessionState;
  signInWithPassword: (login: string, password: string) => Promise<void>;
  signInAsGuest: (displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Экран `/join/:code` заводит сессию сам: сообщает о ней сюда. */
  adopt: (me: MeResponse) => void;
  /**
   * Спросить сервер, кто в сессии, заново.
   *
   * Нужен ровно экрану `/view`: на нём провайдер при монтировании НЕ спрашивает
   * `getMe` (см. эффект ниже), и отказавший обмен кода обязан вернуть состояние
   * из «загрузки» в правду, иначе любой переход с этого экрана повис бы на ней.
   */
  recheck: () => void;
  /**
   * Выход состоялся, а часть заметок о неточностях осталась в очереди на этом
   * устройстве.
   *
   * ⚠️ Молчать об этом нельзя: окно заметки закрывается сразу со словами
   * «Записал», и человек после этого уверен, что заметка дойдёт. Флаг гаснет
   * при следующем входе этого же человека, потому что вход и есть тот самый
   * «следующий раз», когда очередь разберётся.
   */
  unsentNotes: boolean;
}

const Ctx = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: "loading" });
  /**
   * Чьи заметки остались неотправленными, а не просто «остались ли».
   *
   * ⚠️ Хозяин здесь нужен, и это не педантизм. Вторая дверь выхода
   * (`JoinPage`, смена аккаунта) сразу за выходом заводит сессию ДРУГОГО
   * человека, ученика. Флагом-булевом предупреждение погасло бы этим чужим
   * входом через долю секунды, а обещание «отправятся, когда вы войдёте снова»
   * стало бы враньём: очередь отбирается по `userId`, и заметки преподавателя
   * ждут именно его.
   */
  const [unsentFor, setUnsentFor] = useState<string | null>(null);
  /**
   * Кто сидит в сессии прямо сейчас. Ссылкой, а не состоянием: значение нужно
   * ровно один раз, внутри `signOut`, и держать его в зависимостях коллбэка
   * значило бы пересоздавать `signOut` на каждый чих состояния.
   */
  const signedUserId = useRef<string | null>(null);
  /**
   * Кто в сессии этой вкладки, строкой `sessionIdentity`: с ним сравнивается
   * весть соседней вкладки (`onSessionChanged` ниже). Ссылкой по той же
   * причине, что `signedUserId`.
   */
  const identity = useRef<string | null>(null);
  /** Идёт ли просмотр кабинета админом. Ссылкой по той же причине, что выше. */
  const viewing = useRef(false);

  /**
   * Чья очередь заметок разбирается прямо сейчас.
   *
   * ⚠️ Одним эффектом на состояние, а не тремя вызовами по местам входа (ответ
   * `getMe`, вход паролем или гостем, `adopt` с экрана погашения ссылки):
   * забытая четвёртая дверь означала бы не «очередь не поедет», а «заметка
   * уедет под чужой сессией». Автор заметки берётся сервером из сессии, то
   * есть чужая сессия приписывает заметку не тому человеку, и по записи в
   * таблице это уже не отличить. Поэтому источник один: текущее состояние.
   *
   * `null` при `anon` и `loading` это не остановка цикла, а «слать некому»:
   * сам цикл заведён в `main.tsx` безусловно и переживает обе фазы.
   */
  useEffect(() => {
    viewing.current = state.status === "signed" && Boolean(state.me.viewer);
    identity.current = state.status === "signed" ? sessionIdentity(state.me) : null;
    if (state.status === "signed") {
      signedUserId.current = state.me.userId;
      /**
       * ⚠️ В просмотре кабинета админом очереди хозяина нет. Сессия это
       * ПРЕПОДАВАТЕЛЬ, а браузер админа: разобрав здесь очередь, мы отправили
       * бы заметки из этого браузера под чужим именем, а сервер на пишущий
       * запрос всё равно ответит 403, который очередь считает постоянным
       * отказом (12.6 `docs/admin-panel-design.md`).
       */
      if (state.me.viewer) {
        setQueueUser(null);
        // Очереди карточек в просмотре нет вовсе: тренажёр там предпросмотр
        setFlashcardsUser(null);
        return;
      }
      setQueueUser(state.me.userId);
      setFlashcardsUser(state.me.userId);
      // Вход ТОГО ЖЕ человека это и есть обещанный «следующий раз»:
      // предупреждение о его неушедших заметках с этой секунды перестаёт быть
      // правдой. Вход чужого человека его не гасит
      setUnsentFor((pending) => (pending === state.me.userId ? null : pending));
      return;
    }
    setQueueUser(null);
    // Выход по 401 тоже сюда: хозяина нет, но база не трогается (7.4)
    setFlashcardsUser(null);
  }, [state]);

  useEffect(() => {
    /**
     * Общий обработчик 401 на всё приложение. Сессию отзывают мгновенно
     * (кнопка «выйти везде», истечение по абсолютному сроку), и вкладка,
     * узнавшая об этом из любого запроса, обязана показать экран входа, а не
     * пустой список с непонятной ошибкой.
     */
    api.setUnauthorizedHandler(() => setState({ status: "anon" }));

    /**
     * ⚠️ На `/view` сессию заводит обмен кода, и спрашивать о ней раньше нельзя:
     * `getMe` там почти всегда 401 (просмотр открывают в инкогнито), и экран
     * входа мелькнул бы до окончания обмена. Состояние остаётся «загрузкой»,
     * пока экран не позовёт `adopt` или `recheck`.
     */
    if (window.location.pathname === VIEW_PATH) {
      return () => api.setUnauthorizedHandler(null);
    }

    let alive = true;
    api
      .getMe()
      .then((me) => {
        if (alive) setState({ status: "signed", me });
      })
      .catch(() => {
        // Сюда попадает и «нет сессии» (401), и «API недоступен». Разница для
        // этого экрана нулевая: без сессии всё равно показывать вход
        if (alive) setState({ status: "anon" });
      });

    return () => {
      alive = false;
      api.setUnauthorizedHandler(null);
    };
  }, []);

  /**
   * Ответ входа это MeResponse плюс токен. Токен здесь не нужен: он в cookie.
   *
   * ⚠️ Разбираем схемой, а НЕ переписываем поля руками. Список полей руками уже
   * стоил нам своего: он молча ронял `timeZone`, `teaching` и `learning`, то
   * есть только что вошедший ученик до первой перезагрузки видел сайдбар
   * преподавателя, а человек с подтверждённым поясом получал полосу «укажите
   * часовой пояс». `JoinPage` эти поля переносит и объясняет почему, а вход
   * паролем их терял. `MeResponse.parse` заодно снимает `token`: неизвестные
   * ключи zod отбрасывает.
   */
  const adoptSession = useCallback((s: api.SessionLike) => {
    const me = MeResponse.parse(s);
    announceSessionChanged(sessionIdentity(me));
    setState({ status: "signed", me });
  }, []);

  const signInWithPassword = useCallback(
    async (login: string, password: string) => {
      adoptSession(await api.signInWithPassword(login, password));
    },
    [adoptSession],
  );

  const signInAsGuest = useCallback(
    async (displayName: string) => {
      adoptSession(await api.signInAsGuest(displayName));
    },
    [adoptSession],
  );

  const signOut = useCallback(async () => {
    /**
     * Заметки досылаются ПЕРЕД `api.signOut()`, а не просто перед сбросом
     * состояния, и порядок здесь существенный: `api.signOut()` гасит cookie на
     * сервере, а очередь авторизуется той же самой cookie. Досылка после этого
     * вызова получила бы `401` на каждой заметке, то есть не доставила бы
     * ничего и при этом выглядела бы работающей.
     *
     * ⚠️ Кнопок выхода в приложении три, и две последние не забыты: `Shell.tsx`,
     * `JoinPage.tsx` (смена аккаунта при погашении чужой ссылки) и
     * `TeacherInvitePage.tsx` (чужая сессия на странице приглашения) зовут этот же
     * `signOut` из этого же провайдера, поэтому отдельная правка там не нужна и
     * заводить её не надо. Это и есть причина держать доставку тут, а не на
     * кнопке.
     *
     * Неудача досылки выход НЕ отменяет: человек нажал «Выйти», и оставлять его
     * в чужой сессии ради собственных заметок нельзя. Цена названа вслух через
     * `unsentNotes`, а сами заметки лежат в очереди и уедут при следующем входе
     * этого же человека в этом же браузере.
     */
    // Просмотр кабинета очереди не досылает: хозяина у неё нет (эффект выше)
    if (!viewing.current && !(await flushBeforeSignOut())) setUnsentFor(signedUserId.current);
    /**
     * Повторения карточек досылаются тем же порядком и по тем же причинам, без
     * вопроса (7.4). Неудача выход не отменяет и очередь не трогает: она уедет
     * при следующем входе того же человека. Стирается только кэш колод.
     */
    if (!viewing.current) await flushFlashcardsBeforeSignOut();
    try {
      await api.signOut();
    } finally {
      // Очередь остаётся на диске, но её хозяина в этой вкладке больше нет:
      // без сброса следующий вошедший разобрал бы чужие заметки под своим
      // именем
      setQueueUser(null);
      setFlashcardsUser(null);
      // Соседние вкладки этого браузера живут с той же cookie: без вести они
      // продолжили бы досылать очередь карточек под следующей сессией
      signedUserId.current = null;
      announceSessionChanged(null);
      // Даже если запрос не дошёл, на этом устройстве пользователь вышел:
      // cookie сервер уже мог погасить, а показывать после этого кабинет
      // хуже, чем лишний раз показать экран входа
      setState({ status: "anon" });
    }
  }, []);

  /**
   * Вход другого человека в этой вкладке это смена cookie на весь браузер, и
   * соседние вкладки узнают об этом вестью с тем, кто теперь в сессии. Иначе их
   * фоновые очереди слали бы оценки и заметки прежнего человека под новой
   * сессией (7.3 `docs/flashcards-design.md`).
   *
   * ⚠️ Тот же `adopt` получает и свежий `me` того же человека (пояс, учёт,
   * предметы). Весть уходит и тогда, но решает принимающая вкладка: тот же
   * человек её не перезагружает (`reloadOnSessionChange`).
   */
  const adopt = useCallback((me: MeResponse) => {
    announceSessionChanged(sessionIdentity(me));
    setState({ status: "signed", me });
  }, []);

  /**
   * Соседняя вкладка начала или закончила просмотр кабинета, то есть cookie в
   * этом браузере уже другая. Перезагрузка, а не `recheck`: у вкладки может
   * быть открыта доска со своей локальной базой, и надёжно закрыть всё это
   * разом умеет только новая страница. Экран `/view` не трогаем: там свой
   * обмен кода, и он сам решит, куда идти.
   */
  useEffect(
    () =>
      onSessionChanged((incoming) => {
        if (window.location.pathname === VIEW_PATH) return;
        if (reloadOnSessionChange(identity.current, incoming)) window.location.reload();
      }),
    [],
  );

  const recheck = useCallback(() => {
    api
      .getMe()
      .then((me) => setState({ status: "signed", me }))
      .catch(() => setState({ status: "anon" }));
  }, []);

  const value = useMemo<SessionApi>(
    () => ({
      state,
      signInWithPassword,
      signInAsGuest,
      signOut,
      adopt,
      recheck,
      unsentNotes: unsentFor !== null,
    }),
    [state, signInWithPassword, signInAsGuest, signOut, adopt, recheck, unsentFor],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession вне SessionProvider");
  return ctx;
}

/**
 * Кто смотрит этот кабинет, либо `null` у обычной сессии (раздел 12.6
 * `docs/admin-panel-design.md`). Единственный признак просмотра у клиента.
 */
export function useViewer(): { userId: string; displayName: string } | null {
  const { state } = useSession();
  return state.status === "signed" ? (state.me.viewer ?? null) : null;
}
